<script setup lang="ts">
// @ts-nocheck
import { isNil } from "lodash-es";
import {
  never,
  platformModifierKeyOnly,
  primaryAction,
} from "ol/events/condition.js";

import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON.js";

import Point from "ol/geom/Point.js";
import Polygon from "ol/geom/Polygon.js";
import Draw from "ol/interaction/Draw.js";
import Modify from "ol/interaction/Modify.js";
import Translate from "ol/interaction/Translate.js";
import VectorLayer from "ol/layer/Vector.js";

import { Fill, Stroke, Text } from "ol/style";
import CircleStyle from "ol/style/Circle.js";
import Style from "ol/style/Style";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import defaultImage from "./assets/default.png";
import waringIcon from "./assets/waring.png";

import saveIcon from "./assets/保存.png";
import deleteIcon from "./assets/删除.png";
import svgOverlay from "./assets/新弹框.png";
import defaultIcon from "./assets/灭火器.png";
import editIcon from "./assets/编辑.png";
import colorIcon from "./assets/颜色.png";
import DragIcon from "./DragIcon.vue";
import FloorMark from "./floor.vue";
import { GeoMarkProps } from "./geoMark";
import openMapNew from "./openMap.vue";
import type {
  ConfigBottomBarItem,
  ConfigIcon,
  ConfigTopBarItem,
  Device,
  DragEvent,
  DrawEvent,
  FeatureTypeEnum,
  Floor,
  MenuItem,
  OpenLayersFeature,
  OpenParams,
  OptionType,
  QueryDeviceListType,
} from "./geoMark";

defineOptions({ name: "IGeoMark" });

// 组件props定义
const props = defineProps(GeoMarkProps);

// 事件定义
const emit = defineEmits<{
  closeMain: [];
  saveMain: [result: { geojson: string; floor: Floor | undefined }];
}>();

// 警告气泡坐标
const activatedPositionOverlayWaring = ref<number[]>([]);

// 新增响应式数据
const drawInteraction = ref<any>(null);
const modifyInteraction = ref<any>(null);
const translateInteraction = ref<any>(null);
const currentDrawType = ref<string>("Polygon");

// -------------------- 生命周期钩子 --------------------
onMounted(() => {
  window.addEventListener("popstate", (e: PopStateEvent) => {
    document.documentElement.classList.remove("dark");
    e.preventDefault();
  });
});

onUnmounted(() => {
  document.documentElement.classList.remove("dark");
});

// -------------------- 业务状态与交互 --------------------
const activatedList = ref<boolean>(true); // 信息面板tab切换
const activatedFold = ref<boolean>(false); // 信息面板折叠
const FnUpdateActivatedFold = (): void => {
  activatedFold.value = !activatedFold.value;
};

const listFloor = ref<Floor[]>([]);
const listDevice = ref<Device[]>([]);
const listAllDevice = ref<Device[]>([]); // Devices/Facilities
const listTitle = ref<string>("设备信息列表");

const configIcon = ref<ConfigIcon>({});

// 顶部栏配置
const configTopBar = ref<ConfigTopBarItem[]>([
  { name: `单位名：`, icon: "icon-jianzhu02-L", value: "-----" },
  { name: `建筑名：`, icon: "icon-jianzhu03-L", value: "-----" },
  { name: "退出", icon: "icon-fanhui3", value: "-----" },
]);

// 底部栏配置
const configBottomBar = ref<ConfigBottomBarItem[]>([
  { name: "选择", icon: "icon-xuanze" },
  { name: "编辑", icon: "icon-xiugai" },
  { name: "重置", icon: "icon-zhongzhi1" },
  { name: "放大", icon: "icon-fangda" },
  { name: "缩小", icon: "icon-suoxiao" },
  { name: "颜色", icon: "icon-yanse" },
  { name: "区域设置", icon: "icon-shezhi" },
  { name: "原始尺寸", icon: "icon-chicun" },
  { name: "保存", icon: "icon-Save" },
]);

const configBottomBar2 = ref<ConfigBottomBarItem[]>([
  { name: "区域模板", icon: "icon-moban2" },
  { name: "区域绘制", icon: "icon-pencel" },
]);

const activatedMode = computed<string>(() => {
  return props.type || "edit";
});

const activatedBottomBar = ref<string>("选择");
const activatedFloor = ref<Floor | undefined>();
const refFloor = ref<InstanceType<typeof FloorMark> | null>(null);
const alarmType = ref<string>("fire");
const filtrationType = ref<FeatureTypeEnum[]>([]);
// 告警设备 code（string/number/null），空字符串/null 视为无告警
const waring = ref<string | number | null>(null);

// 是否存在有效的告警配置
const hasWaring = computed<boolean>(() => {
  const w = waring.value;
  return w != null && w !== "";
});

// code 是否命中告警设备（单值比较）
const isWaringCode = (code: unknown): boolean => {
  const w = waring.value;
  return w != null && w !== "" && w === code;
};

// 地图相关数据
const imgUrl = ref<string>(defaultImage);
const rrefMap = ref<any>(null);
// openMap 子组件（v-if 条件渲染）是否已挂载就绪，以及就绪前缓存的待初始化楼层。
// 解决首次渲染时 rrefMap 尚未就绪导致矢量标注丢失、视图缩放不生效的问题
const mapReady = ref<boolean>(false);
const pendingInit = ref<Floor | null>(null);
const activatedPositionOverlay = ref<number[]>([]);
const activatedPropertiesOverlay = ref<Record<string, any>>({});
const activatedArrayFeatures = ref<OpenLayersFeature[]>([]);
const activatedFeature = ref<OpenLayersFeature | null>(null);
const move1 = ref<number[]>([]);

// 区域默认颜色（颜色面板选择后，后续绘制/模板使用）
const defaultColor = ref<string>("rgba(255,255,51,0.2)");

// 颜色面板选项
const colorList = ref<{ name: string; value: string }[]>([
  { name: "黄色", value: "rgba(255,255,51,0.2)" },
  { name: "红色", value: "rgba(255,0,0,0.25)" },
  { name: "绿色", value: "rgba(0,255,0,0.25)" },
  { name: "蓝色", value: "rgba(0,120,255,0.25)" },
  { name: "橙色", value: "rgba(255,150,0,0.3)" },
  { name: "紫色", value: "rgba(150,0,255,0.25)" },
]);

// 地图中心、缩放等配置
const center = ref<number[]>([0, 0]);
const zoom = ref<number>(1); // 修改默认缩放级别为1，确保完整显示图片
const projection = ref<string>("EPSG:3857");
const size = ref<number[]>([800, 600]);
const extent = ref<number[]>([0, 0, 800, 600]);

// 选项配置
const configOptionsDeviceType = ref<OptionType[]>([]);
const configOptionsDeviceModel = ref<OptionType[]>([]);
const configOptionsManufacturer = ref<OptionType[]>([]);

// 查询设备列表
const queryDeviceList = ref<QueryDeviceListType>({
  activatedDeviceType: "",
  activatedManufacturer: "",
  activatedDeviceModel: "",
});

// 拖拽相关
const activatedDragItem = ref<Device>();

// 筛选条件
const deviceTypeSet = new Set<string>(); // 设备类型
const manufacturerSet = new Set<string>(); // 生产商
const deviceModelSet = new Set<string>(); // 设备型号

// -------------------- 方法定义 --------------------

/**
 * 创建要素样式
 */
const createAdvancedStyle = (_feature: OpenLayersFeature) => {
  return [
    new Style({
      fill: new Fill({ color: "rgba(255, 255, 255, 0.2)" }),
      stroke: new Stroke({ color: "#ffcc33", width: 2 }),
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color: "#ffcc33" }),
      }),
    }),
  ];
};

/**
 * 初始化高级修改交互
 */
const initAdvancedModify = () => {
  if (!rrefMap.value) return;

  const source = rrefMap.value.getVectorSource();

  // 移除现有的修改交互
  if (modifyInteraction.value) {
    rrefMap.value.getMap().removeInteraction(modifyInteraction.value);
  }

  // 创建新的修改交互
  modifyInteraction.value = new Modify({
    source,
    condition(event: any) {
      return primaryAction(event) && !platformModifierKeyOnly(event);
    },
    deleteCondition: never,
    insertVertexCondition: never,
    style(feature: OpenLayersFeature, resolution: number) {
      // 应用高级样式
      return createAdvancedStyle(feature);
    },
  });

  // 添加修改开始事件
  modifyInteraction.value.on("modifystart", (event: any) => {
    event.features.forEach((feature: OpenLayersFeature) => {
      feature.set(
        "modifyGeometry",
        { geometry: feature.getGeometry().clone() },
        true,
      );
    });
  });

  // 添加修改结束事件
  modifyInteraction.value.on("modifyend", (event: any) => {
    event.features.forEach((feature: OpenLayersFeature) => {
      const modifyGeometry = feature.get("modifyGeometry");
      if (modifyGeometry) {
        feature.setGeometry(modifyGeometry.geometry);
        feature.unset("modifyGeometry", true);
      }
    });
  });

  rrefMap.value.getMap().addInteraction(modifyInteraction.value);
};

/**
 * 初始化平移交互
 */
const initTranslate = () => {
  if (!rrefMap.value) return;

  // 移除现有的平移交互
  if (translateInteraction.value) {
    rrefMap.value.getMap().removeInteraction(translateInteraction.value);
  }

  // 创建新的平移交互
  translateInteraction.value = new Translate({
    condition(event: any) {
      return primaryAction(event) && platformModifierKeyOnly(event);
    },
    layers: [
      rrefMap.value
        .getMap()
        .getLayers()
        .getArray()
        .filter((layer: any) => layer instanceof VectorLayer),
    ],
  });

  rrefMap.value.getMap().addInteraction(translateInteraction.value);
};

/**
 * 初始化绘制交互
 */
const initDraw = (type: string = "Polygon") => {
  if (!rrefMap.value) return;

  // 移除现有的绘制交互
  if (drawInteraction.value) {
    rrefMap.value.getMap().removeInteraction(drawInteraction.value);
  }

  // 创建新的绘制交互
  drawInteraction.value = new Draw({
    source: rrefMap.value.getVectorSource(),
    type: type as any,
  });

  // 添加绘制完成事件
  drawInteraction.value.on("drawend", (event: any) => {
    const feature = event.feature;
    feature.set("color", defaultColor.value);
    feature.set("text", "新绘制区域");

    // 应用高级样式
    feature.setStyle(createAdvancedStyle(feature));
  });

  rrefMap.value.getMap().addInteraction(drawInteraction.value);
};

/**
 * 打开地图主入口，初始化各类数据
 * @param params 业务参数
 */
const open = async (
  params: OpenParams = {
    listFloor: [],
    listDevice: [],
    mode: "edit",
    defaultFloor: null,
    waring: null,
    listTitle: "设备信息列表",
    configIcon: {},
    configTopBar: [],
    type: "edit",
    featureType: ["Areas"] as FeatureTypeEnum[],
  },
): Promise<void> => {
  if (params.featureType && params.featureType.length > 0) {
    filtrationType.value = params.featureType;
  }
  if (params.type) {
    alarmType.value = params.type;
  }
  if (params.listTitle) {
    listTitle.value = params.listTitle;
  }
  listFloor.value = params.listFloor;
  listDevice.value = params.listDevice;
  listAllDevice.value = params.listDevice;
  configIcon.value = params.configIcon || {};
  configTopBar.value = params.configTopBar || [];
  waring.value = params.waring ?? null;
  const defaultFloor = params.defaultFloor
    ? params.defaultFloor
    : listFloor.value[0];
  refFloor.value?.init(listFloor.value, defaultFloor);

  // 提取筛选条件
  const floorList = listFloor.value.map((item: Floor) => item.floor);
  listAllDevice.value
    .filter((ls) => ls.floor != null && floorList.includes(ls.floor))
    .forEach((item: Device) => {
      if (item.deviceTypeName) deviceTypeSet.add(item.deviceTypeName);
      if (item.manufacturerName) manufacturerSet.add(item.manufacturerName);
      if (item.deviceModelName) deviceModelSet.add(item.deviceModelName);
    });
  // 初始化选项配置
  configOptionsDeviceType.value = Array.from(deviceTypeSet)
    .map((type) => ({
      label: type,
      value: type,
      key: type,
    }))
    .filter((e) => !isNil(e.label));
  configOptionsManufacturer.value = Array.from(manufacturerSet)
    .map((manufacturer) => ({
      label: manufacturer,
      value: manufacturer,
      key: manufacturer,
    }))
    .filter((e) => !isNil(e.label));
  configOptionsDeviceModel.value = Array.from(deviceModelSet)
    .map((deviceModel) => ({
      label: deviceModel,
      value: deviceModel,
      key: deviceModel,
    }))
    .filter((e) => !isNil(e.label));

  // 地图初始化交给 openMap 的 @ready 事件：组件挂载完成后再执行，
  // 避免首次渲染时 rrefMap 尚未就绪导致标注丢失、视图缩放不生效（需点两次的问题）
  if (mapReady.value) {
    initMap(activatedFloor.value || defaultFloor);
  } else {
    // openMap 为 v-if 条件渲染（依赖 activatedFloor?.bucketName），
    // 先设置默认楼层以触发其渲染，待 @ready 后再完成数据加载
    activatedFloor.value = defaultFloor;
    pendingInit.value = defaultFloor;
  }
};

/**
 * openMap 子组件挂载完成（@ready）后回调。
 * 此时 rrefMap / getVectorSource / getView 均已可用，可安全初始化地图。
 */
const handleMapReady = (): void => {
  mapReady.value = true;
  const floor = pendingInit.value || activatedFloor.value;
  if (floor) {
    pendingInit.value = null;
    initMap(floor);
  }
};

// 查找告警设备并应用告警效果：
// - 命中告警的要素 → 补上告警气泡（activatedPositionOverlayWaring）
// - centerOnWarning=true（open/initMap 初始化场景）：弹出设备信息面板并定位视图；
//   false（楼层切换场景）：只补气泡，不打扰浏览
// 定位必须放在宏任务中执行：图片加载适配（loadingSource → setCenter/setZoom/fit，
// 以及 openMap 内 extent watch → nextTick calculateAutoZoom）都在微任务链中完成，
// 若定位紧跟在这些逻辑之前执行，会被后续的 fit 全图 / setCenter(图片中心) 覆盖，
// 造成"打开时不自动定位到告警设备、要等鼠标悬停（overlay autoPan）才定位"的现象。
const locateWaringFeature = (centerOnWarning: boolean): void => {
  if (!hasWaring.value || !rrefMap.value) return;
  const features = rrefMap.value.getVectorSource().getFeatures();
  const target = features.find((feature: OpenLayersFeature) =>
    isWaringCode(feature.get("code")),
  );
  if (!target) return;
  const position = target.getGeometry()!.getCoordinates() as number[];
  activatedPositionOverlayWaring.value = position;
  if (centerOnWarning) {
    activatedPositionOverlay.value = position;
    activatedPropertiesOverlay.value = target.getProperties();
    setTimeout(() => {
      rrefMap.value?.getView()?.setCenter(position);
    }, 0);
  }
};

function loadFeaturesAndHandleWarning(
  geojson: any,
  centerOnWarning = true,
): void {
  const features = new GeoJSON().readFeatures(geojson);
  activatedArrayFeatures.value = features;
  rrefMap.value?.getVectorSource().addFeatures(features);
  rrefMap.value?.getVectorSource().on("addfeature", (event: any) => {
    // 新要素添加逻辑
  });

  // 要素添加后再执行告警匹配（addFeatures 同步完成，此处 setTimeout 主要做兜底，
  // 实际定位动作在 locateWaringFeature 内也已通过宏任务后置）
  setTimeout(() => {
    locateWaringFeature(centerOnWarning);
  });
}

async function initMap(defaultFloor: Floor): Promise<void> {
  rrefMap.value?.getVectorSource().clear();
  // loadingSource 内部已获取图片尺寸并设置 extent/imgUrl/视图
  // eslint-disable-next-line no-use-before-define
  await loadingSource(defaultFloor);

  rrefMap.value?.getMap().on("pointermove", (evt: any) => {
    const pixel = rrefMap.value?.getMap().getEventPixel(evt.originalEvent);
    const viewCoordinate = rrefMap.value
      ?.getMap()
      .getCoordinateFromPixel(pixel);
    if (viewCoordinate) {
      move1.value = viewCoordinate;
    }
    if (activatedBottomBar.value === "编辑") {
      return;
    }
    let feature: OpenLayersFeature | undefined;
    if (rrefMap.value?.getMap().hasFeatureAtPixel(pixel)) {
      if (hasWaring.value) {
        activatedPositionOverlay.value = [];
      }
      const viewCoordinate = rrefMap.value
        ?.getMap()
        .getCoordinateFromPixel(pixel);
      if (viewCoordinate) {
        activatedPositionOverlay.value = viewCoordinate;
      }
      feature = rrefMap.value
        ?.getMap()
        .forEachFeatureAtPixel(
          pixel,
          (feature: OpenLayersFeature, layer: any) => {
            return feature;
          },
        );
      if (feature) {
        activatedPropertiesOverlay.value = feature.getProperties();
      }
    } else {
      if (activatedMode.value === "edit") {
        activatedPositionOverlay.value = [];
      }
    }
  });

  rrefMap.value?.getMap().on("contextmenu", (event: any) => {
    activatedFeature.value =
      rrefMap.value
        ?.getMap()
        .forEachFeatureAtPixel(
          event.pixel,
          (feature: OpenLayersFeature) => feature,
        ) || null;
    activatedBottomBar.value = "选择"; // 右键后切换为选择模式
  });

  rrefMap.value?.getVectorSource().on("change", (item: any) => {
    const features = rrefMap.value?.getVectorSource().getFeatures() || [];
    activatedArrayFeatures.value = features;
  });

  // 初始化高级交互
  nextTick(() => {
    initAdvancedModify();
    initTranslate();
    initDraw(currentDrawType.value);
  });
}

/**
 * 获取图片的真实尺寸
 * @param imageUrl 图片URL
 * @returns Promise 返回图片的宽度和高度
 */
// 图片尺寸缓存：已加载过的图片直接读缓存，跳过 getImageDimensions 的异步等待，
// 避免每次切楼层都用 new Image() 重复加载只为拿尺寸（ol-source-image-static 还会再加载一次用于渲染）。
const imageDimensionsCache = new Map<
  string,
  { width: number; height: number }
>();

const getImageDimensions = (
  imageUrl: string,
): Promise<{ width: number; height: number }> => {
  const cached = imageDimensionsCache.get(imageUrl);
  if (cached) return Promise.resolve(cached);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      imageDimensionsCache.set(imageUrl, dims);
      resolve(dims);
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
};

const loadingSource = async (
  data: Floor,
  centerOnWarning = true,
): Promise<void> => {
  // 切换楼层时清空弹窗与选中状态，避免上一楼层的 popup 残留
  activatedPositionOverlay.value = [];
  activatedPositionOverlayWaring.value = [];
  activatedPropertiesOverlay.value = {};
  activatedFeature.value = null;
  rrefMap.value?.calculateAutoZoom();
  // 无楼层数据时，清空设备列表与底图，避免残留上一层的图片
  if (!data) {
    listDevice.value = [];
    activatedFloor.value = undefined;
    rrefMap.value?.getVectorSource().clear();
    imgUrl.value = "";
    return;
  }
  listDevice.value = listAllDevice.value.filter((item) => {
    return item.floor === data.floor;
  });
  activatedFloor.value = data;
  rrefMap.value?.getVectorSource().clear();

  // 先获取图片尺寸，再将 extent 和 imgUrl 在同一同步块内赋值，
  // 确保 Vue 批量更新时 ol-image-layer 的 key 只从 oldUrl|oldExtent → newUrl|newExtent 变一次，
  // 避免中间态 newUrl|oldExtent（新图片拉伸到旧尺寸 → 图标错位，需切换楼层才恢复）。
  const url = data.bucketName || defaultImage;
  let dims: { width: number; height: number };
  try {
    dims = await getImageDimensions(url);
    imgUrl.value = url;
  } catch {
    // 图片加载失败（404/CORS），回退默认图
    try {
      dims = await getImageDimensions(defaultImage);
      imgUrl.value = defaultImage;
    } catch {
      dims = { width: 800, height: 600 };
      imgUrl.value = "";
    }
  }
  // 先设 extent 再设 imgUrl——同一同步块内 Vue 会批量更新，key 只变一次
  size.value = [dims.width, dims.height];
  extent.value = [0, 0, dims.width, dims.height];
  center.value = [dims.width / 2, dims.height / 2];

  // 更新地图视图
  if (rrefMap.value) {
    const view = rrefMap.value.getView();
    if (view) {
      view.setCenter(center.value);
      view.setZoom(zoom.value);
    }
    rrefMap.value.calculateAutoZoom && rrefMap.value.calculateAutoZoom();
  }

  if (data?.bucketName) {
    if (filtrationType.value && filtrationType.value.length > 0) {
      const geoConfig: Record<
        FeatureTypeEnum,
        { geojson?: string; geomType: "Polygon" | "Point" }
      > = {
        Areas: { geojson: data?.geojson, geomType: "Polygon" },
        Devices: { geojson: data?.geojson, geomType: "Point" },
        Facilities: { geojson: data?.facilityGeoJson, geomType: "Point" },
        Cameras: { geojson: data?.videoGeoJson, geomType: "Point" },
      };
      filtrationType.value.forEach((element) => {
        const conf = geoConfig[element];
        if (!conf?.geojson) return;
        const filtered = JSON.parse(conf.geojson).features.filter(
          (feature: any) =>
            feature.geometry && feature.geometry.type === conf.geomType,
        );
        loadFeaturesAndHandleWarning(
          JSON.stringify({ type: "FeatureCollection", features: filtered }),
          centerOnWarning,
        );
      });
    } else {
      // 视频模式
      if (data?.videoGeoJson && alarmType.value === "video") {
        if (!data?.videoGeoJson) {
          return;
        }
        loadFeaturesAndHandleWarning(data?.videoGeoJson, centerOnWarning);
      } else {
        // 普通geojson
        if (!data?.geojson) {
          return;
        }
        loadFeaturesAndHandleWarning(data?.geojson, centerOnWarning);
      }
    }
  }
};

// 切换楼层：保留告警配置（不清空 waring），切到含告警设备的楼层时由
// loadFeaturesAndHandleWarning 自动重新补上告警气泡；centerOnWarning=false 表示切换场景不自动定位
const FnUpdateFloor = (item: Floor): void => {
  loadingSource(item, false);
};

const fnUpdateDrawFeature2 = (event: DrawEvent): void => {
  event.feature.set("color", defaultColor.value);
  event.feature.set("drawType", "lineString");
};

const fnUpdateDrawFeature = (event: DrawEvent): void => {
  event.feature.set("color", defaultColor.value);
  event.feature.set("text", "重点防控区域");
};

const fnOverrideStyle = (feature: OpenLayersFeature, style: any): any => {
  const code = feature.getProperties()?.code;
  const iconUrl = configIcon.value[code] || defaultIcon;
  if (code && iconUrl) {
    const isWarningOrEdit =
      isWaringCode(code) || activatedMode.value === "edit";

    const scale = isWarningOrEdit ? 1 : 0.8; // Use scale instead of width/height for responsive sizing
    // 设备状态（0故障 1报警 2正常 4离线 -1未启用）：以 listDevice 实时数据为准，
    // 缺失时回退图纸 feature 属性（other 中嵌入了设备对象）
    const deviceState =
      listDevice.value.find((item: Device) => item.code === code)?.state ??
      feature.get("other")?.state ??
      feature.get("state");
    const iconStyle = rrefMap.value?.createIcon(iconUrl, scale, deviceState);

    feature.setStyle(iconStyle);
  } else {
    // eslint-disable-next-line no-use-before-define
    updateFeatureStyle(feature);
  }
  return style;
};

const deleteFeature = (feature: OpenLayersFeature): void => {
  rrefMap.value?.getVectorSource().removeFeature(feature);
};

const updateFeatureStyle = (
  feature = activatedFeature.value,
  isSelected = false,
  _offset = 0.4,
): void => {
  if (!feature) {
    return;
  }
  const text = new Text({
    overflow: true,
    textBaseline: "middle",
    font: "normal 14px 微软雅黑",
    text: feature.get("text") || "重点区域",
    fill: new Fill({
      color: "red",
    }),
    backgroundFill: new Fill({
      color: "white",
    }),
    padding: [2, 5, 2, 5],
  });
  const textStyle = new Style({
    text,
    fill: new Fill({
      color: feature.get("color") || "rgba(255,255,51,0.2)",
    }),
    stroke: new Stroke({
      color: feature.get("color") || "rgba(255,255,51,0.2)",
      width: isSelected ? 4 : 1,
    }),
  });
  feature.setStyle(textStyle);
};

const FnSetColor = (color: string): void => {
  defaultColor.value = color;
  if (activatedFeature.value) {
    activatedFeature.value.set("color", color);
    updateFeatureStyle(activatedFeature.value, true);
  }
  activatedBottomBar.value = "选择";
};

const handlerChecked = (key: string): void => {
  activatedBottomBar.value = key;
  switch (key) {
    case "保存":
      // eslint-disable-next-line no-use-before-define
      FnSaveMain();
      break;
    case "重置":
      rrefMap.value?.getVectorSource().clear();
      break;
    case "放大": {
      const view = rrefMap.value?.getView();
      if (view) view.setZoom((view.getZoom() ?? 1) + 1);
      break;
    }
    case "缩小": {
      const view = rrefMap.value?.getView();
      if (view) view.setZoom((view.getZoom() ?? 1) - 1);
      break;
    }
    case "区域模板":
      (function () {
        const feature = new Feature({
          geometry: new Polygon([
            [
              [786.3792005168201, 338.76241851619795],
              [745.2306619461384, 268.6210691239884],
              [720.3979398123276, 226.2914822164155],
              [689.3061489176491, 181.81733917636961],
              [650.6757212309868, 224.32142350364663],
              [635.9358768302526, 239.17937425863389],
              [628.3098681327549, 250.58557144777728],
              [607.7260105988584, 269.24237742792496],
              [657.7235396817343, 346.765203291549],
              [667.2401356802386, 362.85727288146916],
              [709.1159873844781, 428.26685621909996],
              [786.3792005168201, 338.76241851619795],
            ],
          ]),
          text: "重点消防区域仓库",
          color: defaultColor.value,
        });
        rrefMap.value?.getVectorSource().addFeature(feature);
      })();
      break;
    case "原始尺寸":
      rrefMap.value?.getView().setZoom(1);
      break;
    default:
      break;
  }
};

const dragStart = (item: Device): void => {
  activatedDragItem.value = item;
};

const dragEnd = (evt: DragEvent): void => {
  setTimeout(() => {
    const viewCoordinate = move1.value;
    if (activatedDragItem.value && viewCoordinate) {
      const feature = new Feature({
        geometry: new Point([viewCoordinate[0], viewCoordinate[1]]),
        type: "灭火器",
        deviceType: activatedDragItem.value.deviceType,
        code: activatedDragItem.value.code,
        other: activatedDragItem.value,
      });
      rrefMap.value?.getVectorSource().addFeature(feature);
    }
  }, 100);
};

/**
 * 计算设备列表
 */
const listUnitDevice = computed(() => {
  const codeArr = activatedArrayFeatures.value.reduce(
    (acc: string[], item: any) => {
      if (item.values_.geometry.getType() === "Point") {
        acc.push(item.values_.code);
      }
      return acc;
    },
    [],
  ); // 初始化一个空数组来收集 code

  let _listUnitDevice = listDevice.value.filter((item: Device) => {
    return !codeArr.some((code: string) => item.code === code);
  });
  if (queryDeviceList.value.activatedDeviceModel) {
    _listUnitDevice = _listUnitDevice.filter((item: Device) => {
      return (
        item.deviceModelName === queryDeviceList.value.activatedDeviceModel
      );
    });
  }
  if (queryDeviceList.value.activatedDeviceType) {
    _listUnitDevice = _listUnitDevice.filter((item: Device) => {
      return item.deviceTypeName === queryDeviceList.value.activatedDeviceType;
    });
  }
  if (queryDeviceList.value.activatedManufacturer) {
    _listUnitDevice = _listUnitDevice.filter((item: Device) => {
      return (
        item.manufacturerName === queryDeviceList.value.activatedManufacturer
      );
    });
  }
  return _listUnitDevice;
});

/**
 * 关闭主界面
 */
const FnCloseMain = (): void => {
  emit("closeMain");
};

const FnSaveMain = (): void => {
  document.documentElement.classList.remove("dark");
  const features = rrefMap.value?.getVectorSource().getFeatures() || [];
  const jsonObj = new GeoJSON().writeFeatures(features);

  emit("saveMain", { geojson: jsonObj, floor: activatedFloor.value });
};

// 右键菜单配置（回调依赖 rrefMap / activatedFeature / activatedBottomBar / FnSaveMain，需后置声明）
const configMenuItems = ref<MenuItem[]>([
  {
    text: "删除",
    icon: deleteIcon,
    callback: (val: any) => {
      if (rrefMap.value.getVectorSource()) {
        rrefMap.value.getVectorSource().removeFeature(activatedFeature.value);
      }
    },
  },
  {
    text: "编辑",
    icon: editIcon,
    classname: "some-style-class",
    callback: (val: any) => {
      activatedBottomBar.value = "编辑";
    },
  },
  {
    text: "颜色",
    icon: colorIcon,
    classname: "some-style-class",
    callback: (val: any) => {
      activatedBottomBar.value = "颜色";
    },
  },
  {
    text: "保存",
    icon: saveIcon,
    classname: "some-style-class",
    callback: (val: any) => {
      FnSaveMain();
    },
  },
]);

defineExpose({
  open,
});
</script>

<template>
  <div
    id="ypc-map-container"
    class="relative w-full h-full overflow-hidden t-geo-mark"
  >
    <FloorMark ref="refFloor" @change-floor="FnUpdateFloor" />

    <el-card v-if="activatedMode === 'edit'" class="t-top-bar">
      <div class="flex items-center justify-between h-full">
        <div
          v-for="o in configTopBar"
          :key="o.name"
          class="t-top-bar__item"
          :class="{ 't-top-bar__item--exit': o.name === '退出' }"
          @click="FnCloseMain()"
        >
          <i class="iconfont" :class="[o.icon]" />
          {{ o.name }}
          <span v-if="o.name !== '退出'" class="t-top-bar__value">{{
            o.value
          }}</span>
        </div>
      </div>
    </el-card>

    <el-card v-if="activatedMode === 'edit'" class="t-bottom-bar">
      <div class="t-bottom-bar__inner">
        <template v-for="o in configBottomBar" :key="o.name">
          <div
            v-if="o.name !== '颜色' && o.name !== '区域设置'"
            class="t-bottom-bar__item"
            :class="[activatedBottomBar === o.name ? 't-bbar-active' : '']"
            @click="handlerChecked(o.name)"
          >
            <span>
              <i class="iconfont" :class="[o.icon]" />
              {{ o.name }}
            </span>
          </div>

          <el-popover
            v-else-if="o.name === '区域设置'"
            :width="250"
            style="height: 350px !important"
            placement="top"
            trigger="click"
            popper-class="t-popper"
          >
            <template #reference>
              <div
                class="t-bottom-bar__item"
                :class="[activatedBottomBar === o.name ? 't-bbar-active' : '']"
                @click="handlerChecked(o.name)"
              >
                <span>
                  <i class="iconfont" :class="[o.icon]" />
                  {{ o.name }}
                </span>
              </div>
            </template>
            <template #default>
              <div
                v-for="o in configBottomBar2"
                :key="o.name"
                class="t-bottom-bar__item"
                :class="[activatedBottomBar === o.name ? 't-bbar-active' : '']"
                @click="handlerChecked(o.name)"
              >
                <span>
                  <i class="iconfont" :class="[o.icon]" />
                  {{ o.name }}
                </span>
              </div>
            </template>
          </el-popover>

          <el-popover
            v-else
            :width="250"
            style="height: 350px !important"
            placement="top"
            trigger="click"
            popper-class="t-popper"
          >
            <template #reference>
              <div
                class="t-bottom-bar__item"
                :class="[activatedBottomBar === o.name ? 't-bbar-active' : '']"
                @click="handlerChecked(o.name)"
              >
                <span>
                  <i class="iconfont" :class="[o.icon]" />
                  {{ o.name }}
                </span>
              </div>
            </template>
            <template #default>
              <div class="grid grid-cols-3 gap-2 p-1">
                <div
                  v-for="c in colorList"
                  :key="c.value"
                  class="flex flex-col items-center cursor-pointer"
                  @click="FnSetColor(c.value)"
                >
                  <span
                    class="w-8 h-8 rounded border border-gray-300"
                    :style="{ backgroundColor: c.value }"
                  />
                  <span class="text-xs mt-1">{{ c.name }}</span>
                </div>
              </div>
            </template>
          </el-popover>
        </template>
      </div>
    </el-card>

    <el-card
      v-if="activatedMode === 'edit'"
      class="t-info-panel"
      :class="[activatedFold === true ? 't-fold' : 't-unfold']"
    >
      <div class="t-info-panel__header">
        <div class="t-info-panel__tabs">
          <span
            class="t-info-panel__tab"
            :class="{ 't-info-panel__tab--active': activatedList }"
            @click="activatedList = true"
          >
            {{ listTitle }}
          </span>
          <span
            class="t-info-panel__tab"
            :class="{ 't-info-panel__tab--active': !activatedList }"
            @click="activatedList = false"
          >
            标注信息列表
          </span>
        </div>
        <span class="t-info-panel__fold" @click="FnUpdateActivatedFold">
          {{ activatedFold ? "展开" : "收起" }}
        </span>
      </div>

      <div class="t-info-panel__filters">
        <el-select
          v-model="queryDeviceList.activatedDeviceType"
          clearable
          style="width: 32%"
          class="mr-2"
          placeholder="请选择类型"
          :teleported="false"
        >
          <el-option
            v-for="item in configOptionsDeviceType"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>

        <el-select
          v-model="queryDeviceList.activatedDeviceModel"
          clearable
          style="width: 32%"
          class="mr-2"
          placeholder="请选择型号"
          :teleported="false"
        >
          <el-option
            v-for="item in configOptionsDeviceModel"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>

        <el-select
          v-model="queryDeviceList.activatedManufacturer"
          clearable
          style="width: 32%"
          placeholder="请选择厂商"
          :teleported="false"
        >
          <el-option
            v-for="item in configOptionsManufacturer"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>

      <div
        v-if="activatedList"
        class="grid gap-1 mt-4 px-3.5 w-full grid-cols-12"
      >
        <span class="col-span-1">图标</span>
        <span class="col-span-4">编号</span>
        <!-- <span class="col-span-2 text-center">楼层</span> -->
        <span class="col-span-4">{{
          listTitle === "设备信息列表" ? "设备类型" : "设施类型"
        }}</span>
        <span class="col-span-3">位置</span>
      </div>

      <div v-else class="grid gap-1 mt-4 px-3.5 w-full grid-cols-12">
        <span class="col-span-1">序号</span>
        <span class="col-span-1">图标</span>
        <span class="col-span-8">名称</span>
        <span class="col-span-2">操作</span>
      </div>

      <div
        v-if="activatedList"
        style="height: calc(100% - 100px)"
        class="w-full mt-4 pb-5 overflow-x-hidden overflow-y-auto panel"
      >
        <!-- {{ listUnitDevice.length }} -->
        <template v-for="(item, index) in listUnitDevice" :key="index">
          <div
            class="t-info-panel__row grid gap-1 mt-4 px-3.5 w-full grid-cols-12"
            :title="item.deviceTypeName"
          >
            <span class="col-span-1 cursor-pointer">
              <DragIcon
                :icon="configIcon[item.code] || defaultIcon"
                :item="item"
                :map-ref="rrefMap"
                :move1="move1"
                @dragstart="dragStart"
                @dragend="dragEnd"
              />
            </span>
            <span class="col-span-4 break-words">{{ item.code }}</span>
            <span
              class="col-span-4 break-words"
              :title="item.remark || item.deviceTypeName || item.videoName"
            >
              {{ item.remark || item.deviceTypeName || item.videoName }}
            </span>
            <span class="col-span-3 break-words" :title="item.locationDesc">{{
              item.locationDesc || item.addressDesc
            }}</span>
          </div>
        </template>
      </div>

      <div
        v-else
        style="height: calc(100% - 100px)"
        class="w-full mt-4 overflow-x-hidden overflow-y-auto panel"
      >
        <template v-for="(item, index) in activatedArrayFeatures" :key="item">
          <div
            class="t-info-panel__row grid gap-1 mt-4 px-3.5 w-full grid-cols-12"
          >
            <span class="col-span-1">{{ index + 1 }} </span>
            <span class="col-span-1">
              <div
                v-if="item.values_.geometry.getType() === 'Point'"
                class="!w-8 h-full t-image-point" />

              <div
                v-else
                class="!w-8 h-full"
                :style="`background-color:${item.values_.color}`"
            /></span>

            <span class="col-span-8">
              <el-input
                v-if="item.values_.geometry.getType() === 'Point'"
                v-model="item.values_.type"
                size="large"
                clearable
              />
              <el-input
                v-else
                v-model="item.values_.text"
                size="large"
                clearable
                @change="updateFeatureStyle(item)"
              />
            </span>

            <span
              class="flex items-center justify-center col-span-2 cursor-pointer"
              @click="deleteFeature(item)"
            >
              <i class="iconfont icon-shanchu" />
            </span>
          </div>
        </template>
      </div>
    </el-card>

    <openMapNew
      v-if="listFloor.length > 0 && activatedFloor"
      ref="rrefMap"
      api-key="AjtUzWJBHlI3Ma_Ke6Qv2fGRXEs0ua5hUQi54ECwfXTiWsitll4AkETZDihjcfeI"
      :center="center"
      :zoom="zoom"
      :projection="projection"
      :img-url="imgUrl"
      :size="size"
      :extent="extent"
      :config-menu-items="configMenuItems"
      :activated-bottom-bar="activatedBottomBar"
      :waring="hasWaring ? waring : null"
      :activated-position-overlay-waring="activatedPositionOverlayWaring"
      :activated-position-overlay="activatedPositionOverlay"
      :activated-properties-overlay="activatedPropertiesOverlay"
      :list-title="listTitle"
      :alarm-type="alarmType"
      :svg-overlay="svgOverlay"
      :waring-icon="waringIcon"
      :fn-update-draw-feature="fnUpdateDrawFeature"
      :fn-update-draw-feature2="fnUpdateDrawFeature2"
      :fn-override-style="fnOverrideStyle"
      @ready="handleMapReady"
    >
      <template #overlay>
        <template
          v-if="activatedPropertiesOverlay.type && listTitle === '设备信息列表'"
        >
          <div
            class="flex flex-col justify-start text-white opacity-80 t-bg-overlay flex-nowrap"
          >
            <img :src="svgOverlay" alt="" class="absolute w-full h-full" />
            <div
              class="flex t-overlay-item items-center w-full !mt-10 justify-items-start"
            >
              设备编码： {{ activatedPropertiesOverlay.other?.code }}
            </div>

            <div
              class="flex items-center w-full t-overlay-item justify-items-start"
            >
              创建日期： {{ activatedPropertiesOverlay.other?.createTime }}
            </div>

            <div
              class="flex items-center w-full t-overlay-item justify-items-start"
            >
              设备位置：
              <div
                class="w-52 flex justify-start items-center text-gradient2 truncate ..."
                :title="
                  activatedPropertiesOverlay.other?.videoName ||
                  activatedPropertiesOverlay.other?.addressDesc
                "
              >
                {{
                  alarmType === "video"
                    ? activatedPropertiesOverlay.other?.videoName
                    : activatedPropertiesOverlay.other?.addressDesc
                }}
              </div>
            </div>
            <div
              class="flex items-center w-full t-overlay-item justify-items-start"
            >
              设备类型：
              <div
                class="w-52 flex justify-start items-center text-gradient2 truncate ..."
                :title="
                  activatedPropertiesOverlay.other?.remark ||
                  activatedPropertiesOverlay.other?.deviceTypeName ||
                  activatedPropertiesOverlay.other?.facilityTypeName
                "
              >
                {{
                  alarmType === "video"
                    ? "摄像头"
                    : activatedPropertiesOverlay.other?.remark ||
                      activatedPropertiesOverlay.other?.facilityTypeName ||
                      activatedPropertiesOverlay.other?.deviceTypeName
                }}
              </div>
            </div>
          </div>
        </template>

        <!-- 设施信息弹窗 -->
        <template
          v-if="activatedPropertiesOverlay.type && listTitle === '设施信息列表'"
        >
          <div
            class="flex flex-col justify-start text-white opacity-80 t-bg-overlay flex-nowrap"
          >
            <img :src="svgOverlay" alt="" class="absolute w-full h-full" />
            <div
              class="flex t-overlay-item items-center w-full !mt-10 justify-items-start"
            >
              设施编码： {{ activatedPropertiesOverlay.other?.code }}
            </div>

            <div
              class="flex items-center w-full t-overlay-item justify-items-start"
            >
              创建日期： {{ activatedPropertiesOverlay.other?.createTime }}
            </div>

            <div
              class="flex items-center w-full t-overlay-item justify-items-start"
            >
              设施型号： {{ activatedPropertiesOverlay.other?.deviceModelName }}
            </div>
            <div
              class="flex items-center w-full t-overlay-item justify-items-start"
            >
              设施厂商：
              {{ activatedPropertiesOverlay.other?.manufacturerName }}
            </div>
          </div>
        </template>
      </template>
    </openMapNew>
    <img
      v-else
      :src="defaultImage"
      alt="默认底图"
      :style="{ width: `${size[0]}px`, height: `${size[1]}px` }"
    />
  </div>
</template>
